/**
 * Admin-portal locale registry — the admin app's OWN `admin.*` message
 * catalogs, kept separate from the desktop catalogs in @dosia/shared.
 *
 * Why a separate registry (architecture decision, OPT-8 PR-6):
 *   - The shared `setupI18n` (packages/shared) hard-binds its resources to the
 *     desktop LOCALE_REGISTRY (1016 keys) and takes no resources argument, so it
 *     cannot host `admin.*` keys without refactoring desktop-critical code.
 *   - admin-portal-web is a separate Vite bundle → the i18next default singleton
 *     it imports is its OWN module instance, fully isolated from desktop at
 *     runtime. So the admin app safely initializes that singleton with its own
 *     resources (see ./setupI18n.ts).
 *   - The LANGUAGE SET (which 7 languages + endonyms + date locales) is reused
 *     from @dosia/shared (SUPPORTED_LANGUAGE_CODES / LANGUAGES) — single source
 *     of truth for "which languages", so adding a language stays DRY. This file
 *     only maps each code → its admin message catalog.
 *
 * To add a locale: add ./locales/<code>.json here AND the code to the shared
 * registry (packages/shared/src/i18n/registry.ts) so the two stay in lockstep.
 */
import { SUPPORTED_LANGUAGE_CODES, type LanguageCode } from "@dosia/shared";

import zhHans from "./locales/zh-Hans.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import ja from "./locales/ja.json";
import hu from "./locales/hu.json";
import de from "./locales/de.json";
import pl from "./locales/pl.json";

/** code → flat, dot-namespaced (`admin.*`) admin message catalog. */
export const ADMIN_LOCALE_MESSAGES: Record<
  LanguageCode,
  Record<string, string>
> = {
  "zh-Hans": zhHans,
  en,
  es,
  ja,
  hu,
  de,
  pl,
};

/** Base locale — 现有中文即真相源 (mirrors the shared registry base). */
export const ADMIN_BASE_LOCALE: LanguageCode = "zh-Hans";

// Fail loud at module load if the admin registry drifts from the shared
// language set (a missing catalog would otherwise fall back silently).
for (const code of SUPPORTED_LANGUAGE_CODES) {
  if (!ADMIN_LOCALE_MESSAGES[code]) {
    throw new Error(
      `[admin-i18n] missing locale catalog for "${code}" — add src/i18n/locales/${code}.json`,
    );
  }
}
